
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RecipientTypeEnum } from "../../../common/enums/RecipientTypeEnum";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { DialogResult, DialogState, BindDialogReturn, useDialogState, bindDialog, DialogProps } from "../../../hooks/use-dialog-state/UseDialogState";
import FormInput from "../../forms/form-input/FormInput";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import Form from "../../forms/form/Form";
import ResponsiveDialog from "../../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../../unsaved-changes-prompt/UnsavedChangesPrompt";
import { Box, useTheme } from "@mui/material";
import { RecipientsEditDialogInitValues, RecipientsEditDialogReturnValues, RecipientsEditorDialogFormValues, defaultFormValues, mapToForm, mapToModel } from "./RecipientsEditorDialogUtils";
import { differenceWith, isEqual } from "lodash";
import { IFormRecipient } from "../../form-recipient-list/FormRecipientListUtils";
import { useSaveRecipients } from "../../../queries/recipients/UseSaveRecipients";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { IRecipientEditDTO } from "../../../services/recipient-data-service/Models";
import { phoneNumberWithoutCountryCodeRegex } from "../../../common/regex/RegEx";

export type RecipientsEditDialogResult = DialogResult<RecipientsEditDialogReturnValues>;
export type RecipientsEditDialogProps = DialogProps<RecipientsEditDialogInitValues, RecipientsEditDialogResult>;

export default function RecipientEditDialog({
    open,
    onClose,
    values,
}: RecipientsEditDialogProps): JSX.Element {


    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const theme = useTheme();

    const { mutateAsync: saveRecipients } = useQueryNotifications(useSaveRecipients)({
        groupID: routeContext.groupID as number,
        organizationID: values?.organizationID as number,
        onSuccess: (data) => {
            // save success            
            acknowledge(t("saveSuccessMessage", {
                itemName: getObjectTypeName(eLynxObjectTypeEnum.Recipient)
            }));


            onClose && onClose({
                reason: "ok",
                values: {
                    recipients: data,
                    organizationID: values?.organizationID as number
                }
            });
    
        }
    });


    const form = useForm<RecipientsEditorDialogFormValues>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        //setValue,
        handleSubmit,
        formState: {
            isDirty,
            isValid,
            //dirtyFields
        },
        reset,
        watch,
        //trigger
    } = form;

    useEffect(() => {
        if (open) {

            if (values) {
                reset(mapToForm(values));
            }
        } else {
            reset(defaultFormValues);
        }
    }, [reset, values, open]);

    const [
        recipients, 
    ] = watch([
        "recipients"
    ]);

    async function onOkay(data: RecipientsEditorDialogFormValues) {

        const model = mapToModel(data);

        const o3 = values?.recipients.map(o => {
            return {
                id: o.id,
                recipientType: o.recipientType,
                recipientSubType: o.recipientSubType,
                to: o.to
            } as IFormRecipient
        });


        // return only the items that were modified
        const items = differenceWith(model, o3 ?? [], isEqual);


        const updatedRecipients = items.map(o => {

            return {
                recipientID: o.id,
                recipientType: o.recipientType,
                recipientTo: o.to,
                recipientSubType: o.recipientSubType
            } as IRecipientEditDTO;

        });

        await saveRecipients(updatedRecipients);


        // onClose && onClose({
        //     reason: "ok",
        //     values: {
        //         recipients: updatedRecipients
        //     }
        // });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getCancelConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("recipientEditDialog.cancelEditRecipient"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    return (
        <>
            <ResponsiveDialog
                title={t("recipientEditDialog.editRecipient")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    async (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>

                    {recipients.map((o, index) => 
                    
                    
                        o.type === RecipientTypeEnum.Email ? 

                            <FormPropertySection key={o.id} label="" sx={{padding: 1}}>
                                <FormInput
                                    name={`recipients.${index}.emailAddress`}
                                    label={t("recipientEditDialog.emailAddress")}
                                    sx={{
                                        width: 1
                                    }}
                                    rules={{
                                        pattern: {
                                            value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                            message: t("userEditor.steps.general.emailRequired")
                                        },
                                        required: t("userEditor.steps.general.emailRequired")
                                    }}
                                />

                            </FormPropertySection>
                        :
                            <FormPropertySection key={o.id} label="" sx={{padding: 1}}>
                                
                                <FormInput
                                    name={`recipients.${index}.phoneNumber`}                                        
                                    label={o.type === RecipientTypeEnum.Text ? 'Text Phone Number' : 'Voice Phone Number'}
                                    rules={{
                                        pattern: {
                                            // Bug 52761: DOC - Add User Error
                                            value: phoneNumberWithoutCountryCodeRegex,
                                            // value: /^\(\d{3}\) ?\d{3}( |-)?\d{4}|^\d{3}( |-)?\d{3}( |-)?\d{4}/,
                                            message: t('profile.invalidPhone')
                                        },
                                        required: t("userEditor.steps.general.phoneRequired")
                                    }}
                                    sx={{
                                        width: 1
                                    }}
                                    startAdornment={o.countryCode ? 
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                marginRight: 1,
                                                marginTop: theme.spacing(2.25)
                                            }}>
                                            {`+${o.countryCode}`}
                                        </Box> : 
                                        undefined
                                    }
                                />
                            </FormPropertySection>
                        )
                    }

                </Form>
            </ResponsiveDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type RecipientsEditorDialogState = DialogState<RecipientsEditDialogInitValues, RecipientsEditDialogResult>;
export type BindRecipientsEditorDialogReturn = BindDialogReturn<RecipientsEditDialogInitValues, RecipientsEditDialogResult>;

export function useRecipientsEditorDialogState(): RecipientsEditorDialogState {
    return useDialogState();
}

export function bindRecipientsEditorDialog(props: RecipientsEditorDialogState): BindRecipientsEditorDialogReturn {
    return bindDialog(props);
}
